import { Capacitor, CapacitorHttp } from "@capacitor/core";
import { App } from "@capacitor/app";
import { CapacitorUpdater } from "@capgo/capacitor-updater";

// Use the Firebase Hosting origin because Capacitor owns badminta.com as its
// local WebView origin and intercepts same-origin fetches as packaged assets.
const manifestUrl = "https://badminta.web.app/native-updates/production.json";

const cleanVersion = (value = "") => String(value || "").trim();
let updateCheckPromise = null;
let lastUpdateCheckAt = 0;

const semanticVersion = (value = "") => {
  const match = cleanVersion(value).match(/^(\d+)\.(\d+)\.(\d+)/);
  return match ? match.slice(1).map(Number) : null;
};

const compareSemanticVersions = (left, right) => {
  const a = semanticVersion(left);
  const b = semanticVersion(right);
  if (!a || !b) return 0;
  for (let index = 0; index < 3; index += 1) {
    if (a[index] !== b[index]) return a[index] > b[index] ? 1 : -1;
  }
  return 0;
};

const checkForNativeUpdate = async () => {
  const response = await CapacitorHttp.get({
    url: `${manifestUrl}?checkedAt=${Date.now()}`,
    headers: { Accept: "application/json" },
    connectTimeout: 15_000,
    readTimeout: 15_000
  });
  if (response.status < 200 || response.status >= 300) return;
  const manifest = typeof response.data === "string" ? JSON.parse(response.data) : response.data;
  const version = cleanVersion(manifest.version);
  const url = cleanVersion(manifest.url);
  const checksum = cleanVersion(manifest.checksum);
  if (!version || !url || !checksum) return;

  const [current, appInfo] = await Promise.all([
    CapacitorUpdater.current(),
    App.getInfo()
  ]);
  const currentVersion = cleanVersion(current?.bundle?.version);
  if (currentVersion === version) return;
  if (compareSemanticVersions(version, appInfo.version) < 0) return;
  if (currentVersion !== "builtin" && compareSemanticVersions(version, currentVersion) < 0) return;

  const downloaded = await CapacitorUpdater.download({ version, url, checksum });
  // Apply the web bundle as soon as it is verified. `next()` only stages the
  // bundle and was leaving active Play installs on stale UI until a later
  // background/restart cycle.
  await CapacitorUpdater.set({ id: downloaded.id });
};

const runNativeUpdateCheck = ({ force = false } = {}) => {
  if (!Capacitor.isNativePlatform()) return;
  if (!force && Date.now() - lastUpdateCheckAt < 60_000) return;
  if (updateCheckPromise) return;
  lastUpdateCheckAt = Date.now();
  updateCheckPromise = checkForNativeUpdate()
    .catch((error) => {
      console.warn("Native update check was not completed.", error);
    })
    .finally(() => {
      updateCheckPromise = null;
    });
};

export const initialiseNativeUpdates = () => {
  if (!Capacitor.isNativePlatform()) return;

  // Confirm startup before network work so an invalid OTA bundle rolls back.
  void CapacitorUpdater.notifyAppReady();
  window.setTimeout(() => runNativeUpdateCheck({ force: true }), 500);
  window.addEventListener("online", () => runNativeUpdateCheck({ force: true }));
  void App.addListener("appStateChange", ({ isActive }) => {
    if (isActive) runNativeUpdateCheck();
  });
};
